import { Suspense, useEffect, useRef } from 'react';

import Box from '~/common/components/Box';
import Center from '~/common/components/Center';
import { FloatingMenuContainer } from '~/common/components/FMS/FloatingMenuSystem';
import GlobalHotkeys from '~/common/components/Hotkeys/GlobalHotkeys';
import Spinner from '~/common/components/Spinner/index';
import { TestLoanBanner } from '~/common/components/TestLoanBanner/TestLoanBanner';
import useUrl from '~/common/hooks/useUrl';
import ErrorBoundary from '~/pages/Home/ErrorBoundary';
import Footer from '~/pages/Home/Footer';
import { useNavigation } from '~/stores/navigationStore';

import { FeatureFlagged } from '../../FeatureFlagged';
import LeftDrawerLoanSummary from '../../Nav/LeftDrawer/components/LeftDrawerLoanSummary/LeftDrawerLoanSummary';
import RailNav from '../../Nav/Rail/components/RailNav';
import useRailRouteMatches from '../../Nav/Rail/hooks/useRailRouteMatches';
import { BailOut } from '../BailOut';
import {
  ContentWrapper,
  GutterDrawerContainer,
  LEFT_DRAWER_WIDTH,
  LEFT_RAIL_WIDTH,
  MainContentBox,
  PageLayoutContainer,
  RailNavContainer,
  RightContainer,
} from './Styled';

const Fallback = () => (
  <Center width="100%" height="100%">
    <Spinner />
  </Center>
);

const UtilityContainers = ({ children, allCategories }) => (
  <Box data-testid="utilityContainers">
    {children}
    <FeatureFlagged featureFlag="IsFloatingMenuEnabled">
      <FloatingMenuContainer allCategories={allCategories} />
    </FeatureFlagged>
    <FeatureFlagged featureFlag="IsHotkeysEnabled">
      <GlobalHotkeys />
    </FeatureFlagged>
  </Box>
);

export default function PageLayout({
  leftDrawerContent,
  topNav,
  children,
  miscUtilityContainers = null,
}) {
  const { categories: allCategories, selectedAlert } = useNavigation();
  const { isReadOnly } = useUrl();
  const { showRailNav } = useRailRouteMatches();
  const mainContentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainContentRef.current?.scrollTo?.({ top: 0 });
  }, [selectedAlert?.ruleId]);

  return (
    <PageLayoutContainer data-testid="pageLayoutContainer">
      {showRailNav && !isReadOnly && (
        <RailNavContainer
          data-testid="railNavContainer"
          railWidth={LEFT_RAIL_WIDTH}
        >
          <RailNav />
        </RailNavContainer>
      )}
      {!isReadOnly && (
        <GutterDrawerContainer
          data-testId="gutterDrawerContainer"
          leftDrawerWidth={LEFT_DRAWER_WIDTH}
        >
          <LeftDrawerLoanSummary />
          {leftDrawerContent}
        </GutterDrawerContainer>
      )}
      <RightContainer data-testid="mainContainer" isReadonly={isReadOnly}>
        <TestLoanBanner />
        <Box data-testid="topNav">{topNav}</Box>
        <ErrorBoundary>
          <Suspense fallback={<Fallback />}>
            <MainContentBox ref={mainContentRef} data-testid="mainContent">
              <ContentWrapper data-testid="contentWrapper">
                {children}
              </ContentWrapper>
              <Box data-testid="footerContainer">
                <BailOut />
                {!isReadOnly && <Footer />}
              </Box>
            </MainContentBox>
          </Suspense>
        </ErrorBoundary>
      </RightContainer>
      <UtilityContainers allCategories={allCategories}>
        {miscUtilityContainers}
      </UtilityContainers>
    </PageLayoutContainer>
  );
}
